"use client";

import Cookies from "js-cookie";
import { memo, useMemo } from "react";
import { select_item } from "@/lib/gtm";
import { routes } from "@/lib/utils/routes";
import { constants } from "@/lib/utils/constants";
import LeftSection from "./CardSection/LeftSection";
import RightSection from "./CardSection/RightSection";
import CenterSection from "./CardSection/CenterSection";
import InfoIcon from "@/assets/icon/indo-tooltip-icon.svg";
import {
  formatProductCategoriesGA4,
  updateGA4PayloadToSelectItem,
} from "@/lib/utils/functions";

const MemoLeftSection = memo(LeftSection);
const MemoRightSection = memo(RightSection);

const ItemCard = ({
  t,
  vehicle,
  product,
  skuList,
  ga4Index,
  cookiesKeys,
  showSpecific,
  showBranchModal,
  toggleFitmentModal,
  handleAddToTrolley,
  handleSizeSelection,
}) => {
  const item = useMemo(() => {
    let info = {};
    const groups = [];
    product?.items.forEach((p) => {
      if (!info?.id && p.id === product.selected) {
        info = {
          ...p,
          breadcrumbs: product?.breadcrumbs || [],
          brand: product?.brand || "",
        };
      }

      if (p?.size) {
        groups.push({ size: p.size, id: p.id, sku: p.sku });
      }
    });

    const soldInPairs = info?.sold_in_pairs == 1;
    const surchargeItem = info?.surcharge || null;

    info.homeDeliveryStock = info?.quantity > 0;
    info.muteClickCollect = false;
    if (
      info?.branch?.branch_id &&
      info?.branch_stock < 1 &&
      info?.hub_stock < 1 &&
      info?.ndc_stock < 1
    ) {
      info.muteClickCollect = true;
    }

    const ga4Payload = {
      currency: info?.currency,
      value: info?.final_price_without_currency,
      items: [
        {
          quantity: 1,
          index: ga4Index,
          item_id: info?.sku,
          item_name: info?.base_title,
          item_brand: product.brand?.title,
          price: info?.final_price_without_currency,
          discount: info?.amountOff_without_currency,
          ...(Number(info?.amountOff_without_currency) &&
            !info?.specialCoupon_applied && {
              coupon: Cookies.get(cookiesKeys.specialCoupon),
            }),
          ...formatProductCategoriesGA4(info?.breadcrumbs),
        },
      ],
    };

    const brand = {
      ...product.brand,
      brand_tier: product.brand.brand_tier?.toLowerCase(),
    };

    return {
      brand,
      groups,
      ga4Payload,
      soldInPairs,
      surchargeItem,
      info: { ...info, brand },
      showOfferGroup: surchargeItem || soldInPairs,
      detailPageUrl: `${routes.products}/${info?.url_key}`,
      hasProductOffer: info?.productOffer?.label?.length > 0,
    };
  }, [product.selected, product.branch.branch_id]);

  const renderIsFit = useMemo(() => {
    return vehicle && !showSpecific && skuList.includes(item.info.sku);
  }, [vehicle, showSpecific, skuList.length, item.info]);

  const handleLinkClick = () => {
    select_item(
      updateGA4PayloadToSelectItem(
        item.ga4Payload,
        item?.info?.breadcrumbs,
        "Category Products",
      ),
    );
  };

  const addProductToBasket = () => {
    handleAddToTrolley({
      product: {
        sku: item.info?.sku,
        uid: item.info?.product_uid,
        name: item.info?.title_with_brand,
        thumbnail: item.info?.thumbnail,
        soldInPairs: item.info?.sold_in_pairs || 0,
      },
    });
  };

  return (
    <div className="plpProductBox plpBox text-wrap">
      <MemoLeftSection
        t={t}
        info={item.info}
        handleLink={handleLinkClick}
        soldInPairs={item.soldInPairs}
        imageTypes={constants.image_types}
        detailPageUrl={item.detailPageUrl}
        surchargeItem={item.surchargeItem}
        showOfferGroup={item.showOfferGroup}
      />
      <div className="plpContentBox">
        <CenterSection
          t={t}
          info={item.info}
          sizes={item.groups}
          handleLink={handleLinkClick}
          isCompatibleItem={renderIsFit}
          showBranchModal={showBranchModal}
          detailPageUrl={item.detailPageUrl}
          imageTypes={constants.image_types}
          showFitmentInfo={toggleFitmentModal}
          vehicleInfo={vehicle?.vehicle || null}
          hasProductOffer={item.hasProductOffer}
          handleSizeSelection={handleSizeSelection}
        />

        {(item.soldInPairs || item.surchargeItem) && (
          <div className="block lg:hidden mt-3 w-full">
            {item.soldInPairs && (
              <div className="flex items-center gap-2 bg-tertiary-yellow-600 rounded-lg p-[6px]">
                <InfoIcon />
                <p className="w-full max-w-[calc(100%-28px)] text-minor-text-1-mb">
                  {t("sold_unit")}
                </p>
              </div>
            )}
            {item.surchargeItem && (
              <div className="flex xs:items-center gap-2 mt-1 bg-tertiary-yellow-600 rounded-lg p-[6px]">
                <InfoIcon />
                <p className="w-full max-w-[calc(100%-28px)] text-minor-text-1-mb">
                  {t("trade_text", {
                    amount: item.surchargeItem,
                  })}
                </p>
              </div>
            )}
          </div>
        )}
        <MemoRightSection
          t={t}
          info={item.info}
          showBranchModal={showBranchModal}
          imageTypes={constants.image_types}
          addProductToBasket={addProductToBasket}
        />
      </div>
    </div>
  );
};

export default ItemCard;
